import { useCallback, useEffect, useRef, useState } from 'react';
import { reportApi } from '../../../../api/reportApi';
import { getTranscriptionLanguageName } from '../../../../constants/transcriptionLanguages';
import { isRunningStatus } from '../../../../constants/jobStatus';

const TRANSLATION_POLL_MS = 5000;

/** Translations of the session report: the language list, and starting, following and cancelling a translation. */
export function useReportTranslations(id, notify) {
  const [languages, setLanguages] = useState([]);
  const [translatingCode, setTranslatingCode] = useState(null);
  const translationPoll = useRef(null);

  useEffect(() => () => clearInterval(translationPoll.current), []);

  const stopTranslationPoll = () => {
    clearInterval(translationPoll.current);
    translationPoll.current = null;
    setTranslatingCode(null);
  };

  const pollTranslation = useCallback((code, announce) => {
    clearInterval(translationPoll.current);
    setTranslatingCode(code);
    translationPoll.current = setInterval(async () => {
      try {
        const res = await reportApi.getReportLanguages(id);
        const list = res.languages || [];
        setLanguages(list);
        const target = list.find((l) => l.code === code);
        if (target?.status !== 'COMPLETED' && target?.status !== 'FAILED') return;
        stopTranslationPoll();
        if (!announce) return;
        if (target.status === 'COMPLETED') {
          notify({ type: 'success', title: 'Translation complete', message: `Report translated to ${getTranscriptionLanguageName(code)}` });
        } else {
          notify({ type: 'error', title: 'Translation failed', message: target.error_message || 'Unknown error' });
        }
      } catch {
        stopTranslationPoll();
      }
    }, TRANSLATION_POLL_MS);
  }, [id, notify]);

  /** Loads the language list and resumes polling a translation that is still running. */
  const loadLanguages = useCallback(async () => {
    try {
      const res = await reportApi.getReportLanguages(id);
      const list = res.languages || [];
      setLanguages(list);
      const running = list.find((l) => isRunningStatus(l.status));
      if (running && !translationPoll.current) pollTranslation(running.code, false);
    } catch (e) {
      console.warn('Failed to load report languages:', e);
    }
  }, [id, pollTranslation]);

  const translate = useCallback(async (code) => {
    if (!code) return;
    setTranslatingCode(code);
    try {
      await reportApi.translateReport(id, code);
      notify({
        type: 'success',
        title: 'Translation started',
        message: `Report translation to ${getTranscriptionLanguageName(code)} has started. This may take a minute.`,
      });
      pollTranslation(code, true);
    } catch (e) {
      setTranslatingCode(null);
      notify({ type: 'error', title: 'Translation failed', message: e.message });
    }
  }, [id, notify, pollTranslation]);

  const cancelTranslation = useCallback(async (code) => {
    if (!code) return;
    try {
      await reportApi.cancelTranslation(id, code);
      stopTranslationPoll();
      await loadLanguages();
      notify({ type: 'success', title: 'Translation cancelled', message: `Translation to ${getTranscriptionLanguageName(code)} was cancelled.` });
    } catch (e) {
      notify({ type: 'error', title: 'Cancel failed', message: e.message });
    }
  }, [id, loadLanguages, notify]);

  const clearLanguages = useCallback(() => setLanguages([]), []);

  return { languages, translatingCode, loadLanguages, translate, cancelTranslation, clearLanguages };
}
